import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { haptic } from '../../utils/haptics';
import { localizedProduct, optionLabelIn } from '../../utils/shopperText';
import { canAddToCart } from './agentCart';
import WhatsAppLink from '../WhatsAppLink/WhatsAppLink';
import { isAskProduct } from '../../utils/orderMode';
import styles from './agentChat.module.css';

const SIGN = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };

/**
 * The products the agent found, as a strip the shopper scrolls sideways — the storefront's own price.
 * Each card carries a stable `data-zoom-key` (zoomKeyFor), so the page it opens can shrink back into it (utils/zoom),
 * and `onOpen` gets the card itself as the box the page grows out of.
 *
 * A card is a door, a subject and — on the customer side (owner 10-23: "add to cart action"; the portal's cards stay
 * without actions) — something to buy: the card opens the product's page; "Add to cart" (`onAdd`) puts it in the
 * storefront's own cart, asking first which option when it has sizes / colours (out of stock cannot be picked); one
 * quiet "?" (`onAsk`) makes it what the conversation is about. A product the shop sells by ASKING (`orderMode: 'ask'`,
 * ask-us products, 2026-10-25) has no "Add to cart": its action is "Ask on WhatsApp", the shop's link the page builds with
 * the product named (`askHrefFor`) and shows through the same WhatsAppLink every other WhatsApp control uses. Its price is the server's (the storefront card's rule,
 * campaigns applied there) — it is not the shop's campaign-priced ProductCard, hence its own name.
 */
function ChatProductCard({ p: found, sign, hrefFor, onOpen, onAsk, onAdd, askHrefFor, onAskOnWhatsapp, zoomKeyFor, t }) {
  /* The card in the page's language when it carries its translations (utils/shopperText), and everything it hands on —
   * the cart line, the "ask about" subject, the WhatsApp ask — names it in those words. */
  const { i18n } = useTranslation();
  const p = localizedProduct(found, i18n?.language);
  const [picking, setPicking] = useState(false);
  // Each option worded in the page's language from its slot values; the shop's own `label` where it carries none.
  const options = (p.options || []).map((o) => {
    const shown = o && o.values ? optionLabelIn(p, o.values, i18n?.language) : '';
    return shown ? { ...o, label: shown } : o;
  });
  const add = (e) => {
    e.stopPropagation();
    if (options.length) {
      haptic('selection');
      setPicking((x) => !x);
      return;
    }
    onAdd(p, null);
  };
  const pick = (e, o) => {
    e.stopPropagation();
    setPicking(false);
    onAdd(p, o);
  };
  return (
    // The whole card opens the page (the link inside is the door a keyboard and a long-press find); its buttons stop
    // there.
    <div role="listitem" className={styles.card} data-zoom-key={zoomKeyFor ? zoomKeyFor(p) : undefined} onClick={(e) => { if (onOpen) { e.preventDefault(); onOpen(p, e.currentTarget); } }}>
      <a className={styles.cardOpen} href={hrefFor(p.id)}>
        <div className={styles.cardImage}>
          {p.image ? <img src={p.image} alt={p.name} loading="lazy" /> : <span className={styles.cardNoImage} aria-hidden="true">🛍️</span>}
          {p.discountPct ? <span className={styles.badge}>−{p.discountPct}%</span> : null}
        </div>
        <div className={styles.cardName}>{p.name}</div>
        <div className={styles.cardPrice}>
          {p.price !== null && p.price !== undefined && !(isAskProduct(p) && !(p.price > 0)) ? (
            <strong>{p.priceVaries ? `${t('shop.priceFrom', { defaultValue: 'from' })} ` : ''}{sign}{p.price}</strong>
          ) : null}
          {p.compareAt ? <s className={styles.compareAt}>{sign}{p.compareAt}</s> : null}
        </div>
      </a>
      {picking ? (
        <div className={styles.cardOptions} role="group" aria-label={t('agent.chooseOption', { defaultValue: 'Choose an option' })} onClick={(e) => e.stopPropagation()}>
          {options.map((o) => (
            <button key={o.variantId} type="button" className={styles.cardOption} disabled={o.inStock === false} onClick={(e) => pick(e, o)} title={o.inStock === false ? t('agent.outOfStock', { defaultValue: 'Out of stock' }) : undefined}>
              {o.label}
            </button>
          ))}
        </div>
      ) : null}
      <div className={styles.cardActions}>
        {isAskProduct(p) ? (
          askHrefFor && askHrefFor(p) ? (
            <WhatsAppLink
              href={askHrefFor(p)}
              className={styles.cardAction}
              testId={`agent-ask-whatsapp-${p.id}`}
              onClick={(e) => { e.stopPropagation(); if (onAskOnWhatsapp) onAskOnWhatsapp(p); }}
            >
              {t('agent.askOnWhatsapp', { defaultValue: 'Ask on WhatsApp' })}
            </WhatsAppLink>
          ) : null
        ) : onAdd && canAddToCart(p) ? (
          <button type="button" className={styles.cardAction} aria-expanded={options.length ? (picking ? 'true' : 'false') : undefined} onClick={add}>
            {t('agent.addToCart', { defaultValue: 'Add to cart' })}
          </button>
        ) : null}
        {/* A question mark, not words (owner 10-23); its name still says what it does. */}
        {onAsk ? (
          <button type="button" className={styles.askAbout} aria-label={t('agent.askAboutThis', { defaultValue: 'Ask about this' })} title={t('agent.askAboutThis', { defaultValue: 'Ask about this' })} onClick={(e) => { e.stopPropagation(); haptic('selection'); onAsk(p); }}>
            <span aria-hidden="true">?</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function ProductStrip({ products, currency = 'ILS', hrefFor, onOpen, onAsk, onAdd, askHrefFor, onAskOnWhatsapp, zoomKeyFor, t }) {
  if (!products?.length) return null;
  const sign = SIGN[currency] ?? '';
  return (
    <div className={styles.strip} role="list" data-testid="agent-product-strip">
      {products.map((p) => <ChatProductCard key={p.id} p={p} sign={sign} hrefFor={hrefFor} onOpen={onOpen} onAsk={onAsk} onAdd={onAdd} askHrefFor={askHrefFor} onAskOnWhatsapp={onAskOnWhatsapp} zoomKeyFor={zoomKeyFor} t={t} />)}
    </div>
  );
}
